"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateWorklogRulesRequestSchema = exports.worklogRulesResponseSchema = exports.worklogRulesSchema = exports.worklogDayTotalsSchema = exports.worklogTaskRowSchema = exports.worklogFacetsSchema = exports.worklogOverviewSchema = exports.worklogBarSchema = exports.worklogProjectShareSchema = exports.worklogDaySchema = exports.updateWorklogRequestSchema = exports.createWorklogRequestSchema = exports.worklogSchema = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
const working_day_1 = require("../workspace/working-day");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Worklogs — the C11 wire shapes.                                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * ⚠️ **`HH:MM`, a wall-clock time on the entry's own day** — not an instant. Migration 0017 has the
 * argument: the day is `workedOn`, and combining the two needs a timezone the workspace's members
 * may not share.
 */
const wireTimeSchema = zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
exports.worklogSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `wl_0bb16eb3` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('worklog'),
    taskId: zod_1.z.string(),
    taskKey: zod_1.z.string(),
    taskTitle: zod_1.z.string(),
    /** Null for a task with no project — the mockup renders that as *Belső projekt*. */
    projectId: zod_1.z.string().nullable(),
    projectName: zod_1.z.string().nullable(),
    projectColor: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **OS-1 · THE LINE ITEM THIS HOUR BILLS TO, WHICH IS NOT ALWAYS ITS TASK'S.** Several line
     * items may claim one task and each names whose hours it counts, so the author decides. Null with
     * a project beside it is an **unidentified** hour: no line item on the task counts this person on
     * this day. It still counts towards the project.
     */
    lineItemId: zod_1.z.string().nullable(),
    lineItemName: zod_1.z.string().nullable(),
    /**
     * ⚠️ 28 Oct 2026 · **an hour no line item holds** — the reports' *Ismeretlen logolások* and the
     * Logolás grid's *Ismeretlen sortétel*, by the same predicate (`report-list.ts`'s `unrouted`;
     * a task with no project is always one). Not derivable from `lineItemId` alone: a recurring
     * contract's collector task bills without one.
     */
    unknownLineItem: zod_1.z.boolean(),
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    /** C22 · the logger's picture. See `personSchema.avatarUrl`. */
    memberAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    memberAvatarSeed: zod_1.z.string().nullable(),
    workedOn: wireDateSchema,
    /** ⚠️ A STRING, because `numeric` crosses the driver as one. Rendered through `formatHours`. */
    hours: zod_1.z.string(),
    startedAt: wireTimeSchema.nullable(),
    note: zod_1.z.string().nullable(),
    /** C10's values, resolved in — the same reason the task read carries its own. */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ C37 · whether this hour has reached the tracker.                      │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **`null` MEANS *NOT OURS TO SYNC*, WHICH IS NOT THE SAME AS *NOT SENT YET*.** Most hours in
     * most workspaces belong to tasks with no remote issue at all, and drawing a badge on those would
     * put a permanent *pending* on work that is never going anywhere. Only a worklog whose task is
     * linked carries a state.
     *
     * ⚠️ **A state, not an error** (`jira.md` §9.3): the badge sits in the same register as the
     * missing-line-item label, with the reason on hover — not a red failure on somebody's timesheet
     * because an administrator has not connected their Jira.
     */
    /**
     * ⚠️ **`detached` is C39's addition and it is not a failure** (`0084`): the hour is still here and
     * its copy in Jira is gone, because Jira cascades an issue deletion to that issue's worklogs. An
     * hour that originated here is unlinked rather than deleted — Jira held the copy, this is the
     * original — and the badge says so, or somebody has an hour that silently stopped syncing.
     */
    syncState: zod_1.z.enum(['pending', 'synced', 'failed', 'detached']).nullable(),
    /** ⚠️ `code:detail`, for the hover. Never a sentence — the reader's language is not the server's. */
    syncError: zod_1.z.string().nullable(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ P3-5 · whether this hour was claimed as overtime, and at what rate.   │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **THE MULTIPLIER IS THE FROZEN ONE, AND NULL UNTIL THERE IS ONE.** P3-5: an
     * APPROVED claim carries the rate agreed at approval, and re-pricing the type afterwards restates
     * nothing — which is what the settings screen's footnote promises. A list that always read the
     * type would break that promise.
     *
     * ⚠️⚠️ **AND A PENDING CLAIM HAS NO RATE AT ALL — `0147`.** It used to fall back to the
     * type's figure of the moment, so a row read *Függőben · 2×*: a price beside a claim nobody had
     * decided, and one that moved if somebody edited the type. The approver picks the kind now, so
     * until they do there is no type and no multiplier to show.
     *
     * ⚠️ **`null` on the OBJECT means no claim**, not a claim worth nothing. Most hours are
     * ordinary hours.
     */
    overtime: zod_1.z
        .object({
        requestId: zod_1.z.string(),
        /** ⚠️ Null while unpriced — see above. */
        typeName: zod_1.z.string().nullable(),
        /**
         * ⚠️⚠️ **`revoked` TOO — UAT, 27 Oct 2026.** A claim withdrawn by the person who raised it
         * stays the hour's one claim (`overtime_requests_one_per_log`), so the day it sits on came
         * back with a status this enum refused, and the whole time screen failed to parse.
         */
        status: zod_1.z.enum(['pending', 'approved', 'rejected', 'revoked']),
        /** ⚠️ A string: `numeric` crosses the driver as one, and 1.5 × 3 must not become a float. */
        multiplier: zod_1.z.string().nullable(),
    })
        .nullable(),
    version: zod_1.z.number().int(),
});
exports.createWorklogRequestSchema = zod_1.z.object({
    taskId: zod_1.z.uuid(),
    /**
     * ⚠️ **Absent means "me", and that is not laziness.** Logging time for somebody else is a
     * different act with a different authority — `worklog.create` at `own` scope is what most roles
     * hold, so the common case must not require naming yourself. The service refuses another member
     * unless the caller's scope reaches further.
     */
    memberId: zod_1.z.uuid().optional(),
    workedOn: wireDateSchema,
    /** Accepts every form the shared parser takes: `8`, `8h`, `8:00`, `8,0`, `90m`. */
    hours: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]),
    startedAt: wireTimeSchema.nullable().optional(),
    note: zod_1.z.string().max(500).nullable().optional(),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
});
/**
 * ⚠️ **Every field optional except the version, because the day list edits hours IN PLACE.** The
 * mockup's day entries are a single number you type over — so a change of hours must not require
 * resending the note, the date and the task, any of which a stale screen would get wrong.
 */
exports.updateWorklogRequestSchema = zod_1.z.object({
    taskId: zod_1.z.uuid().optional(),
    workedOn: wireDateSchema.optional(),
    hours: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
    startedAt: wireTimeSchema.nullable().optional(),
    note: zod_1.z.string().max(500).nullable().optional(),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    version: zod_1.z.number().int().nonnegative(),
});
/* ── the Time overview's aggregates ───────────────────────────────────────────────────────── */
/**
 * A day in the strip.
 *
 * ⚠️ **Computed server-side, and the totals are STRINGS in `H:MM`.** A browser summing `numeric`
 * strings would reintroduce floating-point drift on exactly the figures somebody bills from, and
 * formatting per call site is how one screen shows `7.5` and the next `7:30`.
 */
exports.worklogDaySchema = zod_1.z.object({
    date: wireDateSchema,
    hours: zod_1.z.string(),
    /** Whether anything at all is logged — so a strip can render a dash rather than `0:00`. */
    hasEntries: zod_1.z.boolean(),
    /**
     * ⚠️⚠️ **C44'S VERDICT, SO THE WEEK STRIP CAN SAY WHETHER A DAY IS DONE.** The strip showed the
     * hours and nothing else, which answers *"how much"* and not *"is this day finished"* — the
     * question somebody opens the screen with. It is the same seven-state kernel the Logolás
     * dashboard and the home chart read, so a green Tuesday here and a red one there is impossible.
     *
     * ⚠️ `null` when the day is somebody else's or outside what the reader may see — the overview is
     * also the PROJECT's, where a verdict about one person would be meaningless.
     */
    verdict: zod_1.z.enum(working_day_1.DAY_VERDICTS).nullable(),
    /** The day's plan, for the tooltip. `null` for a flex day or an unscheduled person. */
    plannedHours: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **WHICH KIND OF DAY OFF, BY NAME — 27 Oct 2026.** Leave and a public holiday share one tint
     * on the strip, and a tester could not tell them apart. The calendar entry's name — *Nemzeti
     * ünnep* — when the workspace calendar shapes the day, and the approved leave's TYPE name when the
     * person is away. Both `null` where the verdict is (`subject` is nobody), and otherwise when
     * nothing applies.
     */
    holidayName: zod_1.z.string().nullable(),
    leaveName: zod_1.z.string().nullable(),
});
/** One row of the project breakdown. */
exports.worklogProjectShareSchema = zod_1.z.object({
    projectId: zod_1.z.string().nullable(),
    name: zod_1.z.string(),
    color: zod_1.z.string().nullable(),
    hours: zod_1.z.string(),
    /** Whole percent of the period's total, 0–100. Rounded once, server-side. */
    percent: zod_1.z.number().int().min(0).max(100),
});
/**
 * A bar in the week or month chart.
 *
 * ⚠️ **The label is built server-side**, because the month names are Hungarian and the week label is
 * a formatted date — both of which are locale decisions the aggregate already has the data for.
 */
exports.worklogBarSchema = zod_1.z.object({
    from: wireDateSchema,
    label: zod_1.z.string(),
    hours: zod_1.z.string(),
    /** Whole percent of the tallest bar, so a chart needs no maximum of its own. */
    percent: zod_1.z.number().int().min(0).max(100),
    /** The period containing today. */
    current: zod_1.z.boolean(),
});
exports.worklogOverviewSchema = zod_1.z.object({
    /** Monday to Sunday of the week containing the requested day. */
    days: zod_1.z.array(exports.worklogDaySchema),
    weekHours: zod_1.z.string(),
    byProject: zod_1.z.array(exports.worklogProjectShareSchema),
    bars: zod_1.z.array(exports.worklogBarSchema),
    barsTotal: zod_1.z.string(),
    /**
     * 16 Oct 2026 · Időrögzítés's *Havi* view — every day of the calendar month containing the
     * requested day, in the same shape and from the same kernel as `days`. Present only when asked
     * for (`?month=1`), so the week strip does not pay for a month it is not drawing.
     */
    monthDays: zod_1.z.array(exports.worklogDaySchema).optional(),
});
/**
 * 31 Oct 2026 · ⚠️ RULE 8 — what the person page's time-log filters may offer, over the list's own
 * filters (`GET /worklogs/facets`). The months, projects and line items the matching hours carry,
 * so a menu never offers a value that empties the list — and is no longer built from the rows on
 * one page, which cannot know what the next page holds.
 */
exports.worklogFacetsSchema = zod_1.z.object({
    /** `yyyy-mm`, newest first. */
    months: zod_1.z.array(zod_1.z.string()),
    projects: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string() })),
    lineItems: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string() })),
});
/**
 * 31 Oct 2026 · ⚠️ RULE 8 — one timesheet row (`GET /worklogs/by-task`): a task, and EVERY hour the
 * filters match on it. The week views page by task rather than by hour, so a row on screen is
 * always whole — its cells, its total and the range edits read the entries it carries, and a task
 * is never split across two pages.
 */
exports.worklogTaskRowSchema = zod_1.z.object({
    taskId: zod_1.z.string(),
    /** The row's hours over the filtered window, as the numeric column renders. */
    hours: zod_1.z.string(),
    /** Newest day first, and within a day oldest entry first — the list's own order. */
    entries: zod_1.z.array(exports.worklogSchema),
});
/**
 * 31 Oct 2026 · ⚠️ RULE 8 — the hours per day over the WHOLE filtered window
 * (`GET /worklogs/days`): the timesheet's column totals and its week total, which a page of rows
 * cannot add up. Only days with hours appear.
 */
exports.worklogDayTotalsSchema = zod_1.z.object({
    days: zod_1.z.array(zod_1.z.object({ day: zod_1.z.string(), hours: zod_1.z.string() })),
    hours: zod_1.z.string(),
});
/* ── C13: the rules, on the wire ───────────────────────────────────────────────────────────── */
/**
 * ⚠️⚠️ **THE CLIENT IS SENT THE RULES, AND THAT IS NOT A SECURITY HOLE — IT IS THE OPPOSITE.**
 * Enforcement is the server's and only the server's (CLAUDE.md rule 6). But a screen that cannot say
 * *"that day would go over 12 hours"* until the request comes back makes somebody type an entry, wait,
 * read a refusal and type it again — and the quick-log's whole point is that it is one gesture.
 *
 * ⚠️ **The screen ALSO gets `earliestWritableDay` as a date it can put in a date input's `min`.**
 * Every rule that refuses a day is a rule the picker can decline to offer, which turns a refusal
 * into an absence.
 */
exports.worklogRulesSchema = zod_1.z.object({
    unit: zod_1.z.enum(['min15', 'min30', 'decimal']),
    maxPerDay: zod_1.z.number().positive().max(24),
    backdateDays: zod_1.z.number().int().min(1),
    backdateNoLimit: zod_1.z.boolean(),
    lockDay: zod_1.z.number().int().min(1).max(28).nullable(),
});
/**
 * The rules as READ, which is the wire shape plus its version.
 *
 * ⚠️⚠️ **C13 SHIPPED A `PUT` THAT REQUIRED A VERSION ITS `GET` NEVER RETURNED**, so the endpoint was
 * only callable by somebody who had queried the database — which is exactly what happened, because
 * the form was C16's and nothing else called it. Found while writing that form.
 *
 * ⚠️ **A separate schema rather than a field on `worklogRulesSchema`.** That shape mirrors
 * `WorklogRules` in `rules.ts`, which is the predicate type `enforceRules`, `roundToUnit` and
 * `isMonthLocked` all take — a wire concern like a version has no business in it.
 */
exports.worklogRulesResponseSchema = exports.worklogRulesSchema.extend({
    version: zod_1.z.number().int(),
});
/**
 * ⚠️ **`version` is required, because these rules are a mutable business row** — CLAUDE.md §3, and
 * a stale write is a 409. Two admins on the settings screen at once is exactly the case.
 */
exports.updateWorklogRulesRequestSchema = zod_1.z.object({
    unit: zod_1.z.enum(['min15', 'min30', 'decimal']),
    maxPerDay: zod_1.z.number().positive().max(24),
    backdateDays: zod_1.z.number().int().min(1),
    backdateNoLimit: zod_1.z.boolean(),
    lockDay: zod_1.z.number().int().min(1).max(28).nullable(),
    version: zod_1.z.number().int().nonnegative(),
});
//# sourceMappingURL=contract.js.map